import { useCallback } from 'react';

/**
 * Custom hook for managing line item additions and removals.
 * 
 * Uses identity-based tracking via `_lineItemId` on each existing line item.
 * - Add: appends a new item WITHOUT `_lineItemId` (backend assigns one)
 * - Remove: splices the item from the local array (backend infers deletion from absence)
 * - No index mapping or deleted indices tracking needed
 *
 * @param {Object} params - Hook parameters
 * @param {Array} params.detailFields - Configured detail fields for line items
 * @param {Object} params.editableResults - Current editable results state
 * @param {Function} params.setEditableResults - State setter for editable results
 * @param {number} params.currentPage - Current pagination page
 * @param {Function} params.setCurrentPage - State setter for current page
 * @param {number} params.itemsPerPage - Number of items per page
 * @param {Object} params.validationErrors - Current validation errors
 * @param {Function} params.setValidationErrors - State setter for validation errors
 * @returns {Object} - Hook interface with add/remove handlers
 */
export const useLineItemManagement = ({
  detailFields,
  editableResults,
  setEditableResults,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  validationErrors,
  setValidationErrors
}) => {
  /**
   * Add a new line item. The new item does NOT get a `_lineItemId` —
   * the backend will assign one during reconciliation.
   */
  const handleAddLineItem = useCallback(() => {
    const newLineItem = {};
    detailFields.forEach(field => {
      newLineItem[field.fieldKey] = '';
    });

    setEditableResults(prev => {
      const updatedLineItems = [...(prev.LineItems || []), newLineItem];

      // Navigate to last page if paginated
      const newTotalItems = updatedLineItems.length;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (newTotalPages > currentPage) {
        setCurrentPage(newTotalPages);
      }

      return {
        ...prev,
        LineItems: updatedLineItems
      };
    });
  }, [detailFields, setEditableResults, currentPage, setCurrentPage, itemsPerPage]);

  /**
   * Remove a line item by its current array index.
   * The backend infers deletion from the item's absence in the submitted array.
   * No index mapping or deleted indices tracking is needed — identity is
   * tracked by `_lineItemId` on each existing item.
   */
  const handleRemoveLineItem = useCallback((lineIndex) => {
    setEditableResults(prev => {
      const lineItems = [...(prev.LineItems || [])];
      lineItems.splice(lineIndex, 1);

      // Handle pagination edge case: if current page becomes empty, go to previous page
      const newTotalItems = lineItems.length;
      const newTotalPages = Math.ceil(newTotalItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }

      return {
        ...prev,
        LineItems: lineItems
      };
    });

    // Clear any validation errors for the removed line item
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`LineItems[${lineIndex}].`)) {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
  }, [setEditableResults, currentPage, setCurrentPage, itemsPerPage, setValidationErrors]);

  // No-op reset (kept for backward compatibility until callers are updated in task 7.2)
  const resetDeletedIndices = useCallback(() => {}, []);
  const clearLineItemChanges = useCallback(() => {}, []);

  return {
    handleAddLineItem,
    handleRemoveLineItem,
    // Legacy interface — these are no-ops/empty values kept for build compatibility.
    // Task 7.2 will remove their usage from ExtractionDetailsView.js.
    deletedOriginalIndices: [],
    resetDeletedIndices,
    lineItemChanges: [],
    clearLineItemChanges
  };
};
